import {
    ICorporationResponse,
    ILocationIssueSummaryResponse,
} from '@kcftech/prometheustypescriptapiclient';
import { useCorporationIssuesSummary } from 'custom_hooks/data_hooks/corporation/useCorporationIssuesSummary';
import { CorporationIssuesTable } from 'pages/Corporation/issues/CorporationIssuesTable';
import { Container, HeaderContainer, Title } from 'pages/Corporation/issues/styled';
import { getSortFunction, SortField, SortState } from 'pages/Corporation/issues/utils';
import { CorporationOverviewFilters } from 'pages/Corporation/types';
import { forwardRef, memo, useImperativeHandle, useMemo, useState } from 'react';
import { OverviewStatusLozenge } from 'shared/lozenge/OverviewStatusLozenge';

export type CorporationIssuesWidgetProps = {
    corporation: ICorporationResponse;
    filters?: CorporationOverviewFilters;
};

export type CorporationIssuesWidgetRef = {
    refetch: () => Promise<unknown>;
};

function CorporationIssuesWidget(
    { corporation, filters }: CorporationIssuesWidgetProps,
    ref: React.ForwardedRef<CorporationIssuesWidgetRef>
) {
    const [sort, setSort] = useState<SortState>({
        field: 'newIssueCount',
        direction: 'desc',
    });

    const { data, isFetching, isLoading, refetch } = useCorporationIssuesSummary({
        corporationId: corporation.Id,
        filters,
    });

    useImperativeHandle(ref, () => ({
        refetch,
    }));

    const handleSort = (field: SortField) => {
        setSort((prev) => {
            if (prev.field === field) {
                if (prev.direction === 'asc') {
                    return { field: 'newIssueCount', direction: 'desc' };
                }
                return { field, direction: 'asc' };
            }
            return { field, direction: 'desc' };
        });
    };

    const sortedData = useMemo<ILocationIssueSummaryResponse[]>(() => {
        if (!data) {
            return [];
        }
        return [...data].sort(getSortFunction(sort.field, sort.direction));
    }, [data, sort]);

    return (
        <Container>
            <HeaderContainer>
                <Title>Issues</Title>
                <OverviewStatusLozenge>PAST 2 WEEKS</OverviewStatusLozenge>
            </HeaderContainer>
            <CorporationIssuesTable
                sort={sort}
                onSort={handleSort}
                data={sortedData}
                isLoading={isLoading}
                isFetching={isFetching}
            />
        </Container>
    );
}

const memoCorporationIssuesWidget = memo(
    forwardRef<CorporationIssuesWidgetRef, CorporationIssuesWidgetProps>(
        CorporationIssuesWidget
    )
);
export { memoCorporationIssuesWidget as CorporationIssuesWidget };
